import './App.css';
import React, { useState, useReducer, useRef, useEffect } from 'react';
import useFitText from "use-fit-text";
import { Temporal } from '@js-temporal/polyfill';

function computeFullPlan(plan) {
    var totalDist = undefined;
    var totalDistTrue = 0;
    var totalMin = undefined;
    var totalMinTrue = 0;

    const legs = plan.legs.map(leg => {
        if (leg.dist !== undefined) {
            totalDist = (totalDist || 0) + parseInt(leg.dist);
        }

        if (leg.wind.dir !== undefined && leg.wind.vel !== undefined && leg.tas !== undefined && leg.tc !== undefined) {
            const dir = parseInt(leg.wind.dir);
            const vel = parseInt(leg.wind.vel);
            const tas = parseInt(leg.tas);
            const tc = parseInt(leg.tc);
            const awc = (dir - tc) / 180 * Math.PI;

            const wca = Math.asin(Math.sin(awc) * vel / tas);
            const gs = Math.cos(wca) * tas - Math.cos(awc) * vel;

            const wcad = Math.round(wca / Math.PI * 180);
            const th = ((tc + wcad + 359) % 360) + 1;
            const mh = ((th + (leg.var ? parseInt(leg.var) : 0) + 359) % 360) + 1;
            const ch = ((mh + (leg.dev ? parseInt(leg.dev) : 0) + 359) % 360) + 1;

            var ete = undefined;
            var eteStr = undefined;
            if (leg.dist !== undefined && leg.dist > 0) {
                const dist = parseInt(leg.dist);
                const duration = dist / gs * 60;
                ete = Temporal.Duration.from({ minutes: Math.round(duration) }).round({ largestUnit: 'hour' });
                eteStr = `${ete.hours.toString().padStart(2, '0')}:${ete.minutes.toString().padStart(2, '0')}`;

                totalDistTrue += dist;
                totalMin = (totalMin || new Temporal.Duration()).add(ete);
                totalMinTrue += duration;

            }

            return {
                ...leg,
                wca: (wcad > 0 ? '+' : '') + wcad,
                th: th.toString().padStart(3, '0'),
                mh: mh.toString().padStart(3, '0'),
                ch: ch.toString().padStart(3, '0'),
                ete,
                eteStr,
                gs: Math.round(gs)
            }
        }
        return leg;
    });

    var totalFuel = undefined;
    if (plan.fuel.ground !== undefined || plan.fuel.climb !== undefined ||
        plan.fuel.cruise !== undefined || plan.fuel.approach !== undefined ||
        plan.fuel.reserve !== undefined) {
        const or = v => v ? parseFloat(v) : 0;
        totalFuel = or(plan.fuel.ground) + or(plan.fuel.climb) + or(plan.fuel.cruise) +
            or(plan.fuel.approach) + or(plan.fuel.reserve);
    }
    return {
        ...plan,
        legs,
        totalDist,
        avgGs: totalMin && Math.round(totalDistTrue / totalMinTrue * 60),
        totalEte: totalMin && `${totalMin.hours.toString().padStart(2, '0')}:${totalMin.minutes.toString().padStart(2, '0')}`,
        totalFuel
    };
}

function planReducer(state, action) {
    switch (action.type) {
        case 'setLeg':
            return legReducer(state, action);
        case 'setFuel':
            return {
                ...state,
                fuel: {
                    ...state.fuel,
                    [action.subtype]: action[action.subtype]
                }
            };
        default:
            throw new Error('unknown action');
    }
}

function legReducer(state, action) {
    var oldLeg = state.legs[action.idx];
    var newLeg;
    switch (action.subtype) {
        case 'windDir':
            newLeg = {
                ...oldLeg,
                wind: {
                    ...oldLeg.wind,
                    dir: action.dir
                }
            };
            break;
        case 'windVel':
            newLeg = {
                ...oldLeg,
                wind: {
                    ...oldLeg.wind,
                    vel: action.vel
                }
            };
            break;
        case 'altitude':
        case 'tas':
        case 'tc':
        case 'var':
        case 'dev':
        case 'dist':
            newLeg = {
                ...oldLeg,
                [action.subtype]: action[action.subtype]
            };
            break;
        default:
            throw new Error('unknown leg action');
    }

    return {
        ...state,
        legs: state.legs.map((leg, idx) => idx === action.idx ? newLeg : leg)
    };
}

function makeEmptyLeg(isClimb) {
    return {
        altitude: isClimb ? 'C' : undefined,
        wind: {
            dir: undefined,
            vel: undefined
        },
        tas: undefined,
        tc: undefined,
        var: undefined,
        dev: undefined,
        dist: undefined,
    }
}

function App() {
    const [partialPlan, dispatch] = useReducer(planReducer, {
        legs: [makeEmptyLeg(true), ...[...Array(8)].map(() => makeEmptyLeg(false))],
        takeoff: undefined,

        fuel: {
            ground: undefined,
            climb: undefined,
            cruise: undefined,
            approach: undefined,
            reserve: undefined,
        }
    });
    const plan = computeFullPlan(partialPlan);

    return (
        <div>
            <div className="page first">
                <div className="header">
                    <div className="left">
                        <div className="title">
                            <div className="name sm-caps">Freeway Airport Navigation Log</div>
                            <div className="facility">Flight Training Center</div>
                        </div>
                        <table className="info">
                            <tbody>
                                <tr>
                                    <td>3900 Church Road  Bowie, Maryland 20721</td>
                                    <td>W00</td>
                                    <td>Elevation 168'</td>
                                </tr>
                                <tr>
                                    <td>Phone: 301•390•6424</td>
                                    <td>Runway 18-36: 2,420 x 40</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div className="right">
                        <div className="clearance">
                            <div>Clearance Info: &#123;(C) Cleared to. (R) Route. (A) Altitude. (F) Frequency. (T) Transponder&#125;</div>
                            <textarea></textarea>
                        </div>
                        <div className="takeoff-time">
                            <span className="sm-caps">Time Off: </span>
                        </div>
                    </div>
                </div>
                <div className="log-container box">
                    <div className="log-header">
                        <div className="huge sm-caps" style={{ gridArea: "1 / 1 / 5 / 2" }}>Check Points</div>

                        <div style={{ gridArea: "1 / 2 / 3 / 3" }}>VOR</div>
                        <div className="tiny sep" style={{ gridArea: "3 / 2 / 4 / 3" }}>Ident</div>
                        <div className="tiny" style={{ gridArea: "4 / 2 / 5 / 3" }}>Freq</div>

                        <div className="sm-caps" style={{ gridArea: "1 / 3 / 5 / 4" }}>OBS</div>
                        <div className="sm-caps" style={{ gridArea: "1 / 4 / 5 / 5" }}>Altitude</div>

                        <div style={{ gridArea: "1 / 5 / 3 / 6" }}>Wind</div>
                        <div className="tiny sep" style={{ gridArea: "3 / 5 / 4 / 6" }}>Dir</div>
                        <div className="tiny" style={{ gridArea: "4 / 5 / 5 / 6" }}>Vel</div>

                        <div className="sm-caps" style={{ gridArea: "1 / 6 / 5 / 7" }}>TAS</div>

                        <div className="sep" style={{ gridArea: "1 / 7 / 3 / 8" }}>TC</div>
                        <div style={{ gridArea: "3 / 7 / 5 / 8" }}>WCA</div>

                        <div className="sep" style={{ gridArea: "1 / 8 / 3 / 9" }}>TH</div>
                        <div style={{ gridArea: "3 / 8 / 5 / 9" }}>VAR</div>

                        <div className="sep" style={{ gridArea: "1 / 9 / 3 / 10" }}>MH</div>
                        <div style={{ gridArea: "3 / 9 / 5 / 10" }}>DEV</div>

                        <div className="sm-caps" style={{ gridArea: "1 / 10 / 5 / 11" }}>Compass<br />Heading</div>
                        <div style={{ gridArea: "1 / 11 / 5 / 12" }}>Dist<br />Leg</div>

                        <div style={{ gridArea: "1 / 12 / 3 / 13" }}>GS</div>
                        <div className="tiny sep" style={{ gridArea: "3 / 12 / 4 / 13" }}>EST</div>
                        <div className="tiny" style={{ gridArea: "4 / 12 / 5 / 13" }}>ACT</div>

                        <div className="tiny sep" style={{ gridArea: "3 / 13 / 4 / 14" }}>ETE</div>
                        <div className="tiny" style={{ gridArea: "4 / 13 / 5 / 14" }}>ATE</div>

                        <div className="tiny sep" style={{ gridArea: "3 / 14 / 4 / 15" }}>ETA</div>
                        <div className="tiny" style={{ gridArea: "4 / 14 / 5 / 15" }}>ATA</div>
                    </div>
                    <div className="log-body">
                        <div className="checkpoint-list grid-container">
                            {[...Array(10)].map((_, idx) => (
                                <div className="grid-container" key={`checkpoint-${idx}`}>
                                    <AutoTextArea
                                        className="checkpoint"
                                        style={{
                                            gridArea: `${2 * idx + 2} / 1 / ${2 * idx + 4} / 2`
                                        }}
                                    />
                                </div>
                            )
                            )}
                        </div>
                        <div className="leg-list grid-container">
                            {
                                plan.legs.map((leg, idx) => <Leg key={`leg-${idx}`} leg={leg} idx={idx} dispatch={dispatch} />)
                            }
                        </div>

                        <div className="total-wrapper grid-container">
                            <div className="total-label">Total</div>
                            <div className="total-dist">{plan.totalDist}</div>
                            <div className="total-gs">{plan.avgGs}</div>
                            <div className="total-time">{plan.totalEte}</div>
                            <div className="total-eta"></div>
                        </div>
                    </div>
                    <div className="log-misc">
                        <div className="log-fuel grid-container">
                            <div className="misc-header sm-caps">Fuel Required</div>
                            <div className="label grid-container">
                                <label className="cruise-rpm">
                                    <span>Cruise RPM:</span>
                                    <input type="number" />
                                </label>
                                <div>Run-up, Taxi, Take-Off</div>
                                <div>Climb</div>
                                <div>Cruise</div>
                                <div>Approach&nbsp;<span style={{ fontSize: '0.6em' }}>(10 min)</span></div>
                                <div>Reserve</div>
                                <div>Total</div>
                            </div>
                            <div className="content grid-container">
                                <label className="cruise-fb">
                                    <span>Cruise GPH:</span>
                                    <input type="number" step="0.01" />
                                </label>
                                {
                                    ['ground', 'climb', 'cruise', 'approach', 'reserve'].map((item, idx) => (
                                        <input
                                            type="number"
                                            style={{ gridRow: `${idx + 3} / ${idx + 4}` }}
                                            min="0"
                                            step="0.01"
                                            key={`fuel-${item}`}
                                            value={plan.fuel[item]}
                                            onChange={event =>
                                                dispatch({
                                                    type: 'setFuel',
                                                    subtype: item,
                                                    [item]: (event.target.validity.valid && event.target.value.length) ? event.target.value : undefined
                                                })
                                            }
                                        />
                                    ))
                                }
                                <div className="sans-serif">{plan.totalFuel}</div>
                            </div>
                        </div>
                        <div className="log-dep grid-container">
                            <div className="misc-header sm-caps">Departure Airport Information</div>
                            <div className="label grid-container">
                                <div>Unicom / CTAF<br />Gnd / Clearance</div>
                                {
                                    ['Twr', 'Dep', 'Rwys & Lengths', 'Field Elevation', 'Pattern Alt.'].map((item, idx) => (
                                        <div
                                            style={{ gridRow: `${idx + 4} / ${idx + 5}` }}
                                            key={`dep-label-${idx}`}
                                        >{item}
                                        </div>
                                    ))
                                }
                            </div>
                            <div className="content grid-container">
                                {
                                    [...Array(6)].map((_, idx) => (
                                        <AutoTextArea
                                            className="free-area"
                                            key={`dep-content-${idx}`}
                                            style={{ gridRow: `${idx + 3} / ${idx + 4}` }}
                                        />
                                    ))
                                }
                            </div>
                        </div>
                        <div className="log-arr grid-container">
                            <div className="misc-header sm-caps">Arrival Airport Information</div>
                            <div className="label grid-container">
                                {
                                    ['Approach', 'ATIS / AWOS / ASOS', 'Tower / CTAF', 'Ground / UNICOM', 'Rwys & Lengths', 'Field Elevation', 'Pattern Alt.'].map((item, idx) => (
                                        <div
                                            style={{ gridRow: `${idx + 2} / ${idx + 3}` }}
                                            key={`arr-label-${idx}`}
                                        >{item}
                                        </div>
                                    ))
                                }
                            </div>
                            <div className="content grid-container">
                                {
                                    [...Array(7)].map((_, idx) => (
                                        <AutoTextArea
                                            className="free-area"
                                            key={`arr-content-${idx}`}
                                            style={{ gridRow: `${idx + 2} / ${idx + 3}` }}
                                        />
                                    ))
                                }
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div className="page">
                <div className="back box">
                    <div className="sec-header">Weather Log</div>
                    <div className="weather">
                        <div className="empty"></div>
                        <div className="weather-header grid-container">
                            <div style={{ gridArea: "1 / 2 / 2 / 4" }}>Ceiling, Visibility, and Surface Wind</div>
                            <div style={{ gridArea: "2 / 2 / 3 / 3" }}>Reported</div>
                            <div style={{ gridArea: "2 / 3 / 3 / 4" }}>Forecast</div>

                            <div style={{ gridArea: "1 / 4 / 2 / 6" }}>Winds Aloft</div>
                            <div style={{ gridArea: "2 / 4 / 3 / 5", paddingLeft: "3em" }}>Dir</div>
                            <div style={{ gridArea: "2 / 5 / 3 / 6" }}>Vel</div>

                            <div style={{ gridArea: "1 / 6 / 3 / 7" }}>Icing and Freezing<br />Level</div>
                            <div style={{ gridArea: "1 / 7 / 3 / 8" }}>Turbulence and<br />Cloud Tops</div>
                        </div>

                        <div className="weather-loc grid-container">
                            <div style={{ gridRow: "3 / 6" }}>Departure</div>
                            <div style={{ gridRow: "6 / 9" }}>En Route</div>
                            <div style={{ gridRow: "9 / 12" }}>Destination</div>
                        </div>

                        <div className="weather-free grid-container">
                            {
                                [...Array(3)].map((_, idx) => (
                                    <div className="grid-container" key={`weather-free-${idx}`}>
                                        <AutoTextArea
                                            style={{
                                                gridRow: `${3 * idx + 3} / ${3 * idx + 6}`,
                                                gridColumn: "2 / 3"
                                            }}
                                        />
                                        <AutoTextArea
                                            style={{
                                                gridRow: `${3 * idx + 3} / ${3 * idx + 6}`,
                                                gridColumn: "3 / 4"
                                            }}
                                        />
                                        <AutoTextArea
                                            style={{
                                                gridRow: `${3 * idx + 3} / ${3 * idx + 6}`,
                                                gridColumn: "6 / 7"
                                            }}
                                        />
                                        <AutoTextArea
                                            style={{
                                                gridRow: `${3 * idx + 3} / ${3 * idx + 6}`,
                                                gridColumn: "7 / 8"
                                            }}
                                        />
                                    </div>
                                ))
                            }
                        </div>

                        <div className="weather-wind-aloft grid-container">
                            {
                                [...Array(3)].map((_, idx) => (
                                    <div key={`wind-aloft-${idx}`} className="grid-container">
                                        <div
                                            style={{ gridRow: `${3 * idx + 3} / ${3 * idx + 6}` }}
                                            className="dir"
                                        >
                                            {
                                                [3000, 6000, 9000].map((alt, idx2) => (
                                                    <div
                                                        key={`aloft-dir-${idx}-${idx2}`}
                                                    >
                                                        <span>{alt}</span>
                                                        <div>
                                                            <input
                                                                type="number"
                                                                min="0"
                                                                max="360"
                                                                step="1"
                                                            />
                                                        </div>
                                                    </div>
                                                ))
                                            }
                                        </div>
                                        <div
                                            style={{ gridRow: `${3 * idx + 3} / ${3 * idx + 6}` }}
                                            className="vel"
                                        >
                                            {
                                                [...Array(3)].map((_, idx2) => (
                                                    <div key={`aloft-vel-${idx}-${idx2}`}>
                                                        <input
                                                            type="number"
                                                            min="0"
                                                            step="1"
                                                        />
                                                    </div>
                                                ))
                                            }
                                        </div>
                                    </div>
                                ))
                            }
                        </div>
                    </div>
                    <div className="plan-notams-header">
                        <div className="sec-header">Flight Plan</div>
                        <div className="sec-header">Notes & NOTAMs</div>
                    </div>
                    <div className="plan-notams">
                        <div className="plan">
                            <div className="line-1 line">
                                <div className="plan-type">
                                    <div>Type</div>

                                    <label className="left"><input type="checkbox" /></label>
                                    <div>IFR</div>

                                    <label className="left"><input type="checkbox" /></label>
                                    <div>VFR</div>

                                    <label className="left"><input type="checkbox" /></label>
                                    <div>SFRA</div>
                                </div>
                                <LabeledInput
                                    input={{
                                        type: "text",
                                        className: "mono uppercase"
                                    }}
                                >
                                    Aircraft ID
                                </LabeledInput>
                                <LabeledInput
                                    input={{
                                        type: "text"
                                    }}
                                >
                                    Aircraft Type /<br />Special Equip.
                                </LabeledInput>
                                <LabeledInput
                                    input={{
                                        type: "number",
                                        min: "0"
                                    }}
                                >
                                    True<br />Airspeed
                                </LabeledInput>
                                <LabeledInput
                                    input={{
                                        type: "text",
                                        className: "mono uppercase"
                                    }}
                                >
                                    Departure<br />Point
                                </LabeledInput>
                                <SplitLabeledInput
                                    className="dep-time"
                                    input={{
                                        type: "text",
                                        pattern: "\\d?\\d:\\d\\d[zZ]?",
                                        className: "uppercase"
                                    }}
                                    header="Departure Time"
                                    label1="Proposed"
                                    label2="Actual"
                                />
                                <LabeledInput
                                    input={{
                                        type: "text"
                                    }}
                                >
                                    Cruising Altitude
                                </LabeledInput>
                            </div>
                            <LabeledTextarea className="line-2">Route of Flight</LabeledTextarea>
                            <div className="line-3 line">
                                <LabeledTextarea>Destination (Airport & City)</LabeledTextarea>
                                <SplitLabeledInput
                                    input={{
                                        type: "number",
                                        min: "0"
                                    }}
                                    header="EST. Time en Route"
                                    label1="Hours"
                                    label2="Minutes"
                                />
                                <LabeledTextarea>Remarks</LabeledTextarea>
                            </div>
                            <div className="line-4 line">
                                <SplitLabeledInput
                                    input={{
                                        type: "number",
                                        min: "0"
                                    }}
                                    header="Fuel on Board"
                                    label1="Hours"
                                    label2="Minutes"
                                />
                                <LabeledInput
                                    input={{
                                        type: "text",
                                        className: "mono uppercase"
                                    }}
                                >
                                    Alternate Airport (if required)
                                </LabeledInput>
                                <LabeledTextarea>Pilots Name, Aircraft Home Base, Phone Number</LabeledTextarea>
                                <LabeledInput
                                    input={{
                                        type: "number",
                                        min: "1"
                                    }}
                                >
                                    No. Aboard
                                </LabeledInput>
                            </div>
                            <div className="line-5 line">
                                <LabeledInput
                                    input={{
                                        type: "text"
                                    }}
                                >
                                    Color of Aircraft
                                </LabeledInput>
                                <div className="phones">
                                    <div className="left-right">
                                        <span>FSS: 1-800-WX-BRIEF (99-27433)</span>
                                        <span>POTOMAC: 1-866-599-3874</span>
                                    </div>
                                    <div className="left-right">
                                        <span>LEESBURG FSS: 1-866-225-7410 (SFRA)</span>
                                        <span>PATTERN WORK COMPLETION: 540-351-6129</span>
                                    </div>
                                    <div>
                                        <label>
                                            <span>SQUAWK CODE:</span>
                                            <input type="text" pattern="[0-7]{4}" className="squawk" />
                                        </label>
                                    </div>
                                    <div>"I need to file an SFRA flight plan. I am familiar with the Washington DC special flight rules and procedures"</div>
                                </div>
                            </div>
                        </div>
                        <div className="notams">
                            {
                                [...Array(8)].map((_, idx) => (
                                    <AutoTextArea
                                        key={`notam-${idx}`}
                                        style={{ gridRow: `${idx + 1} / ${idx + 2}` }}
                                    />
                                ))
                            }
                        </div>
                    </div>
                    <Notes />
                </div>
            </div>
        </div>
    );
}

function Leg(props) {
    return (
        <div className="grid-container">
            <input
                style={{
                    textTransform: "uppercase",
                    fontFamily: "monospace",
                    gridColumn: "2 / 3",
                    gridRow: `${props.idx * 2 + 3} / ${props.idx * 2 + 4}`
                }}
                className="half brder"
                type="text"
            />
            <input
                style={{
                    gridColumn: "2 / 3",
                    gridRow: `${props.idx * 2 + 4} / ${props.idx * 2 + 5}`
                }}
                className="dash-above half btm brder"
                type="text"
            />

            <div
                style={{
                    gridColumn: "3 / 4",
                    gridRow: `${props.idx * 2 + 3} / ${props.idx * 2 + 5}`
                }}
                className="btm marker brder"
            >
                <input min="0" max="360" step="1" type="number" />
            </div>

            <div
                style={{
                    gridColumn: "4 / 5",
                    gridRow: `${props.idx * 2 + 3} / ${props.idx * 2 + 5}`
                }}
                className={`btm brder altitude ${(props.leg.altitude && (props.leg.altitude.toUpperCase() === 'C' || props.leg.altitude.toUpperCase() === 'D')) ? 'arrow' : ''}`}
            >
                <input
                    type="text"
                    pattern="([cC]|[dD]|-?\d+)"
                    disabled={props.idx === 0}
                    value={props.leg.altitude}
                    onChange={event =>
                        props.dispatch({ type: 'setLeg', subtype: 'altitude', idx: props.idx, altitude: event.target.validity.valid ? event.target.value.toUpperCase() : undefined })
                    }
                />
                {props.leg.altitude && props.leg.altitude.toUpperCase() === 'C' && <div className="climb"></div>}
                {props.leg.altitude && props.leg.altitude.toUpperCase() === 'D' && <div className="descent"></div>}
            </div>

            <input
                style={{
                    gridColumn: "5 / 6",
                    gridRow: `${props.idx * 2 + 3} / ${props.idx * 2 + 4}`
                }}
                className="half brder"
                min="0"
                max="360"
                step="1"
                type="number"
                value={props.leg.wind.dir}
                onChange={event => props.dispatch({ type: 'setLeg', subtype: 'windDir', idx: props.idx, dir: event.target.validity.valid ? event.target.value : undefined })}
            />
            <input
                style={{
                    gridColumn: "5 / 6",
                    gridRow: `${props.idx * 2 + 4} / ${props.idx * 2 + 5}`
                }}
                className="dash-above half btm brder"
                min="0"
                step="1"
                value={props.leg.wind.vel}
                onChange={event => props.dispatch({ type: 'setLeg', subtype: 'windVel', idx: props.idx, vel: event.target.validity.valid ? event.target.value : undefined })}
                type="number"
            />

            <input
                style={{
                    gridColumn: "6 / 7",
                    gridRow: `${props.idx * 2 + 3} / ${props.idx * 2 + 5}`
                }}
                className="btm brder"
                min="1"
                step="1"
                value={props.leg.tas}
                onChange={event => props.dispatch({ type: 'setLeg', subtype: 'tas', idx: props.idx, tas: event.target.validity.valid ? event.target.value : undefined })}
                type="number"
            />

            <input
                style={{
                    gridColumn: "7 / 8",
                    gridRow: `${props.idx * 2 + 3} / ${props.idx * 2 + 4}`
                }}
                className="half brder"
                min="0"
                max="360"
                step="1"
                value={props.leg.tc}
                onChange={event => props.dispatch({ type: 'setLeg', subtype: 'tc', idx: props.idx, tc: event.target.validity.valid ? event.target.value : undefined })}
                type="number"
            />
            <div
                style={{
                    gridColumn: "7 / 8",
                    gridRow: `${props.idx * 2 + 4} / ${props.idx * 2 + 5}`
                }}
                className="dash-above half btm brder"
            >{props.leg.wca}</div>

            <div
                style={{
                    gridColumn: "8 / 9",
                    gridRow: `${props.idx * 2 + 3} / ${props.idx * 2 + 4}`
                }}
                className="half brder"
            >{props.leg.th}</div>
            <input
                style={{
                    gridColumn: "8 / 9",
                    gridRow: `${props.idx * 2 + 4} / ${props.idx * 2 + 5}`
                }}
                className="dash-above half btm brder"
                step="1"
                value={props.leg.var}
                onChange={event => props.dispatch({ type: 'setLeg', subtype: 'var', idx: props.idx, var: event.target.validity.valid ? event.target.value : undefined })}
                type="number"
            />

            <div
                style={{
                    gridColumn: "9 / 10",
                    gridRow: `${props.idx * 2 + 3} / ${props.idx * 2 + 4}`
                }}
                className="half brder"
            >{props.leg.mh}</div>
            <input
                style={{
                    gridColumn: "9 / 10",
                    gridRow: `${props.idx * 2 + 4} / ${props.idx * 2 + 5}`
                }}
                className="dash-above half btm brder"
                step="1"
                value={props.leg.dev}
                onChange={event => props.dispatch({ type: 'setLeg', subtype: 'dev', idx: props.idx, dev: event.target.validity.valid ? event.target.value : undefined })}
                type="number"
            />

            <div
                style={{
                    gridColumn: "10 / 11",
                    gridRow: `${props.idx * 2 + 3} / ${props.idx * 2 + 5}`
                }}
                className="btm brder marker"
            ><div>{props.leg.ch}</div></div>

            <input
                style={{
                    gridColumn: "11 / 12",
                    gridRow: `${props.idx * 2 + 3} / ${props.idx * 2 + 5}`
                }}
                className="btm brder"
                value={props.leg.dist}
                onChange={event => props.dispatch({ type: 'setLeg', subtype: 'dist', idx: props.idx, dist: (event.target.value && event.target.validity.valid) ? event.target.value : undefined })}
                min="0"
                type="number"
            />

            <div
                style={{
                    gridColumn: "12 / 13",
                    gridRow: `${props.idx * 2 + 3} / ${props.idx * 2 + 4}`
                }}
                className="half brder"
            >{props.leg.gs}</div>
            <div
                style={{
                    gridColumn: "12 / 13",
                    gridRow: `${props.idx * 2 + 4} / ${props.idx * 2 + 5}`
                }}
                className="dash-above half btm brder"
            ></div>

            <div
                style={{
                    gridColumn: "13 / 14",
                    gridRow: `${props.idx * 2 + 3} / ${props.idx * 2 + 4}`
                }}
                className="half brder"
            >{props.leg.eteStr}</div>
            <div
                style={{
                    gridColumn: "13 / 14",
                    gridRow: `${props.idx * 2 + 4} / ${props.idx * 2 + 5}`
                }}
                className="dash-above half btm brder"
            ></div>

            <div
                style={{
                    gridColumn: "14 / 15",
                    gridRow: `${props.idx * 2 + 3} / ${props.idx * 2 + 4}`,
                    borderRight: 'none'
                }}
                className="half brder"
            ></div>
            <div
                style={{
                    gridColumn: "14 / 15",
                    gridRow: `${props.idx * 2 + 4} / ${props.idx * 2 + 5}`,
                    borderRight: 'none'
                }}
                className="dash-above half btm brder"
            ></div>
        </div>
    )
}

function AutoTextArea(props) {
    const textareaEl = useRef(null);
    const testEl = useRef(null);

    useEffect(() => {
        if (textareaEl.current && testEl.current) {
            const textarea = textareaEl.current;
            const test = testEl.current;
            const recalcSize = () => {
                if (textarea.value === '') {
                    textarea.style.fontSize = `${textarea.clientHeight - 5}px`;
                    return;
                }
                const maxHeight = textarea.clientHeight;
                const maxWidth = textarea.clientWidth;
                test.textContent = textarea.value + (textarea.value.endsWith('\n') ? 'l' : '');
                let low = 0.1, high = 200;
                while (high - low > 0.01) {
                    const mid = (high + low) / 2;
                    test.style.fontSize = `${mid}px`;
                    if (test.scrollHeight > maxHeight || test.scrollWidth > maxWidth) {
                        high = mid;
                    }
                    else {
                        low = mid;
                    }
                }
                textarea.style.fontSize = `${low}px`;
            };
            textareaEl.current.addEventListener('input', recalcSize);
            const media = window.matchMedia('print');
            media.addEventListener('change', recalcSize);

            return () => {
                textarea.removeEventListener('input', recalcSize);
                media.removeEventListener('change', recalcSize);
            }
        }
    }, [textareaEl, testEl]);

    return (
        <>
            <textarea
                style={props.style}
                className={props.className}
                ref={textareaEl}
            />
            <div
                className={`${props.className} pseudo-textarea`}
                ref={testEl}
                style={props.style}
            ></div>
        </>
    )
}

function Notes() {
    const { fontSize, ref } = useFitText({ maxFontSize: 150 });
    const forceUpdate = useReducer(() => ({}), {})[1];

    return (
        <div className="notes">
            <span className="notes-label">Notes: </span>
            <div
                ref={ref}
                style={{ fontSize }}
                className="notes-content"
                contentEditable="true"
                onInput={event => {
                    if (event.target.textContent.length === 0) {
                        event.target.innerHTML = ' ';
                    }
                    forceUpdate();
                }}
                onPaste={event => {
                    event.preventDefault();
                    document.execCommand('insertText', false, event.clipboardData.getData('text'));
                    forceUpdate();
                }}
                suppressContentEditableWarning="true"
            > </div>
        </div>
    );
}

function LabeledInput(props) {
    const [valid, setValid] = useState(true);
    return (
        <div className={`labeled-input ${valid ? '' : 'invalid'}`}>
            <div className="labeled-input-label">{props.children}</div>
            <input
                {...props.input}
                className={`labeled-input-input ${props.input && (props.input.className || '')}`}
                onChange={(event) => setValid(event.target.validity.valid || event.target.value === "")}
            />
        </div>
    );
}

function SplitLabeledInput(props) {
    return (
        <div className={`split-labeled-input ${props.className || ''}`}>
            <div className="split-labeled-input-header">{props.header}</div>
            <div className="split-labeled-input-body grid-container">
                <LabeledInput input={props.input}>{props.label1}</LabeledInput>
                <LabeledInput input={props.input}>{props.label2}</LabeledInput>
            </div>
        </div>
    )
}

function LabeledTextarea(props) {
    return (
        <div className={`labeled-textarea ${props.className || ''}`}>
            <div className="labeled-textarea-label">{props.children}</div>
            <AutoTextArea
                {...props.textarea}
                className={`labeled-textarea-textarea ${(props.textarea && props.textarea.className) || ''}`}
            />
        </div>
    );
}

export default App;
